import { area as polygonArea, bbox, offsetPolygon, polysIntersect, pointInPoly, rotatePt } from "./geometry";
import { nestPieces } from "./nesting";

const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

export function convexHull(pts) {
  const p = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const lower = [];
  for (const q of p) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0) lower.pop(); lower.push(q); }
  const upper = [];
  for (const q of p.reverse()) { while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0) upper.pop(); upper.push(q); }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

const shift = (pts, dx, dy) => pts.map(([x, y]) => [x + dx, y + dy]);
const hit = (placedInf, polys) => polys.some((b) => placedInf.some((P) => polysIntersect(P, b)));
const norm = (pts) => { const b = bbox(pts); return shift(pts, -b.minX, -b.minY); };
const clear = (spacing) => 2 * spacing + 0.5;  // start offset safely outside the inflated outlines (mitred corners stick out past `spacing`)
const pack = (placed) => { const all = placed.flatMap((p) => p.world), bb = bbox(all); return { placed, hull: convexHull(all), w: bb.w, h: bb.h, worlds: placed.map((p) => p.world) }; };

// Slide polygons along `axis` (0 = x, 1 = y) toward the placed pieces from `far` down to the closest non-colliding offset (binary search).
function slide(placedInf, polys, axis, fixed, far, near) {
  const at = (d) => polys.map((b) => (axis === 0 ? shift(b, d, fixed) : shift(b, fixed, d)));
  if (hit(placedInf, at(far))) return null;
  let lo = near, hi = far;
  for (let i = 0; i < 14; i++) { const mid = (lo + hi) / 2; if (hit(placedInf, at(mid))) lo = mid; else hi = mid; }
  return hi;
}

// Tightest arrangement of 2+ glass pieces: the first stays at rotation 0 at the origin, each next piece is rotated/slid until it interlocks.
export function groupLayout(list, spacing = 0.25, maxWidth = 36) {
  // the biggest piece anchors the block as uploaded (door glass lies along the film); smaller pieces nest around it
  const order = list.map((pts, i) => ({ i, a: Math.abs(polygonArea(pts)) })).sort((x, y) => y.a - x.a).map((o) => o.i);
  if (order.some((i, k) => i !== k)) {
    const lay = groupLayout(order.map((i) => list[i]), spacing, maxWidth);
    if (!lay) return null;
    const placed = [], worlds = [];
    order.forEach((orig, k) => { placed[orig] = lay.placed[k]; worlds[orig] = lay.worlds[k]; });
    return { ...lay, placed, worlds };
  }
  const first = list[0];
  const a = norm(first);
  const placed = [{ rot: 0, x: -bbox(first).minX, y: -bbox(first).minY, world: a }];
  const placedInf = [offsetPolygon(a, spacing)];
  for (const pts of list.slice(1)) {
    const ab = bbox(placed.flatMap((p) => p.world));
    let best = null;
    for (const rot of [0, 180, 90, 270]) {
      const rb0 = pts.map((p) => rotatePt(p, rot));
      const rbb = bbox(rb0);
      const b = shift(rb0, -rbb.minX, -rbb.minY);
      const steps = 8;
      const consider = (dx, dy) => {
        const world = shift(b, dx, dy);
        const bb = bbox(placed.flatMap((p) => p.world).concat(world));
        const lo = Math.min(bb.w, bb.h), hi = Math.max(bb.w, bb.h);
        // area, with a nudge toward compact (square-ish) blocks and a hard penalty when the block can't fit across the film
        const score = bb.w * bb.h * (1 + 0.05 * (hi / Math.max(lo, 1))) * (bb.h > bb.w ? 1.15 : 1) * (lo > maxWidth ? 10 : 1);  // low across-film height = film saved
        if (!best || score < best.score) best = { score, rot, x: ab.minX + dx - rbb.minX, y: ab.minY + dy - rbb.minY, world };
      };
      for (let i = 0; i <= steps; i++) {
        const ty = ab.minY + (ab.h - rbb.h) * (i / steps);
        const d = slide(placedInf, [b], 0, ty, ab.maxX + clear(spacing), ab.minX - rbb.w);
        if (d !== null) consider(d - ab.minX, ty - ab.minY);
        const tx = ab.minX + (ab.w - rbb.w) * (i / steps);
        const dy = slide(placedInf, [b], 1, tx, ab.maxY + clear(spacing), ab.minY - rbb.h);
        if (dy !== null) consider(tx - ab.minX, dy - ab.minY);
      }
    }
    if (!best) return null;
    placed.push({ rot: best.rot, x: best.x, y: best.y, world: best.world });
    placedInf.push(offsetPolygon(best.world, spacing));
  }
  return pack(placed);
}

const biggest = (polys) => polys.reduce((m, w) => (Math.abs(polygonArea(w)) > Math.abs(polygonArea(m)) ? w : m), polys[0]);

// Same-side glass the way it sits on the car (DigiCut row): the door as drawn, the small pane (vent / quarter) aligned on the door's
// straight belt edge and slid in from the end where the slanted edges interlock (the tighter block). 3+ pieces fall back to the generic block.
export function beltLayout(list, spacing = 0.25) {
  if (list.length === 1) { const b = bbox(list[0]); return pack([{ rot: 0, x: -b.minX, y: -b.minY, world: norm(list[0]) }]); }
  if (list.length !== 2) return groupLayout(list, spacing, 1e9);
  const big = Math.abs(polygonArea(list[0])) >= Math.abs(polygonArea(list[1])) ? 0 : 1, small = 1 - big;
  const ab = bbox(list[big]), a0 = norm(list[big]), bb0 = bbox(list[small]), b0 = norm(list[small]);
  const ty = straightEdgeOnTop(a0) ? 0 : ab.h - bb0.h, inf = [offsetPolygon(a0, spacing)];
  const tall = centerEnd(a0);  // the pane sits at the door's short (tapered) end, as on the car; both ends tried when the door is even
  const right = () => slide(inf, [b0], 0, ty, ab.w + clear(spacing), -bb0.w), left = () => slide(inf, [b0], 0, ty, -bb0.w - clear(spacing), ab.w);
  let best = null;
  for (const dx of tall === "right" ? [left()] : tall === "left" ? [right()] : [right(), left()]) {
    if (dx === null) continue;
    const world = shift(b0, dx, ty), w = bbox(a0.concat(world)).w;
    if (!best || w < best.w - 1e-6) best = { dx, world, w };
  }
  if (!best) return groupLayout(list, spacing, 1e9);
  const sx = -Math.min(0, best.dx);
  const placed = [];
  placed[big] = { rot: 0, x: -ab.minX + sx, y: -ab.minY, world: shift(a0, sx, 0) };
  placed[small] = { rot: 0, x: best.dx - bb0.minX + sx, y: ty - bb0.minY, world: shift(best.world, sx, 0) };
  return pack(placed);
}

// Left and Right the way DigiCut stacks them across the film: the two straight belt edges meet in the middle, roof lines face out
// (a side drawn belt-up is turned 180° so its belt edge faces the other piece), slid together until the belt edges touch.
// Placements: L's first, then R's.
export function mirrorStack(L, R, spacing = 0.25) {
  const lt = straightEdgeOnTop(biggest(L.worlds)), rt = straightEdgeOnTop(biggest(R.worlds));
  // top piece: belt edge at its bottom (straight edge NOT on top); bottom piece: belt edge on top
  let top = L, bot = R, rotTop = 0, rotBot = 0, swapped = false;
  if (lt && !rt) { top = R; bot = L; swapped = true; }
  else if (lt && rt) rotTop = 180;
  else if (!lt && !rt) rotBot = 180;
  const turn = (lay, rot) => lay.worlds.map((w) => (rot ? w.map((p) => rotatePt(p, rot)) : w));
  const A = turn(top, rotTop), abb = bbox(A.flat()), inf = A.map((w) => offsetPolygon(w, spacing));
  const B = turn(bot, rotBot), rbb = bbox(B.flat());
  let best = null;
  for (const dx of [abb.minX - rbb.minX, abb.maxX - rbb.maxX, (abb.minX + abb.maxX - rbb.minX - rbb.maxX) / 2]) {
    const polys = B.map((w) => shift(w, dx, 0));
    const dy = slide(inf, polys, 1, 0, abb.maxY - rbb.minY + clear(spacing), abb.minY - rbb.minY);  // from below, up until the roof lines meet
    if (dy === null) continue;
    const worlds = polys.map((w) => shift(w, 0, dy)), bb = bbox(A.flat().concat(worlds.flat()));
    if (!best || bb.h < best.bb.h - 1e-6 || (Math.abs(bb.h - best.bb.h) <= 1e-6 && bb.w < best.bb.w)) best = { dx, dy, worlds, bb };
  }
  if (!best) return groupLayout(L.worlds.concat(R.worlds), spacing, 1e9);
  const sx = -best.bb.minX, sy = -best.bb.minY;
  const move = (p, rot, dx, dy) => (rot ? { rot: (p.rot + 180) % 360, x: -p.x + dx, y: -p.y + dy } : { rot: p.rot, x: p.x + dx, y: p.y + dy });
  const topPlaced = top.placed.map((p, i) => ({ ...move(p, rotTop, sx, sy), world: shift(A[i], sx, sy) }));
  const botPlaced = bot.placed.map((p, i) => ({ ...move(p, rotBot, best.dx + sx, best.dy + sy), world: shift(best.worlds[i], sx, sy) }));
  return pack(swapped ? botPlaced.concat(topPlaced) : topPlaced.concat(botPlaced));
}

// Left/Right of the same window: one DigiCut block (see mirrorStack). `maxWidth` is kept for callers; the nester decides the turn.
export const pairLayout = (aPts, bPts, spacing) => mirrorStack(beltLayout([aPts], spacing), beltLayout([bPts], spacing), spacing);

// ---------- Auto nesting for door + pane combo rows (owner's pick "F — shortest film wins") ----------
// Film objective: the shortest run along the roll (x) that still fits across the film (y ≤ maxWidth), then the narrower block.
const filmScore = (bb, maxWidth) => bb.w * 100 + bb.h + (bb.h > maxWidth + 1e-6 ? 1e6 : 0);
const flat = (placed) => placed.flatMap((p) => p.world);
const AUTO_STEPS = 16;
const COARSE = 0.03;  // collision tests run on outlines thinned to 0.03" (arc samples dropped) — the placements keep the exact outline

// Ramer–Douglas–Peucker on a closed outline (split at the two farthest-apart vertices).
export function thin(pts, tol = COARSE) {
  if (pts.length <= 12) return pts;
  const dist = (p, a, b) => { const vx = b[0] - a[0], vy = b[1] - a[1], l2 = vx * vx + vy * vy; if (l2 < 1e-12) return Math.hypot(p[0] - a[0], p[1] - a[1]); const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / l2)); return Math.hypot(p[0] - a[0] - vx * t, p[1] - a[1] - vy * t); };
  const rdp = (seg) => {
    if (seg.length < 3) return seg;
    let k = 0, far = 0;
    for (let i = 1; i < seg.length - 1; i++) { const d = dist(seg[i], seg[0], seg[seg.length - 1]); if (d > far) { far = d; k = i; } }
    if (far <= tol) return [seg[0], seg[seg.length - 1]];
    return rdp(seg.slice(0, k + 1)).slice(0, -1).concat(rdp(seg.slice(k)));
  };
  let b = 0, far = -1;
  for (let i = 0; i < pts.length; i++) { const d = Math.hypot(pts[i][0] - pts[0][0], pts[i][1] - pts[0][1]); if (d > far) { far = d; b = i; } }
  const first = rdp(pts.slice(0, b + 1)), second = rdp(pts.slice(b).concat([pts[0]]));
  return first.slice(0, -1).concat(second.slice(0, -1));
}

// Slide `pts` (at each rotation in `rots`) in toward the placed pieces from every side along `axes`, at AUTO_STEPS offsets; the
// lowest `score(bb, world)` wins. Returns a placement {rot, x, y, world} (world = rotatePt(pts, rot) + (x, y)) or null.
function placeAround(placed, pts, rots, score, spacing, axes = [0, 1]) {
  const worlds = flat(placed), inf = placed.map((p) => offsetPolygon(thin(p.world), spacing)), ab = bbox(worlds), c = clear(spacing);
  const coarse = thin(pts);
  let best = null;
  for (const r of rots) {
    const rp = r ? pts.map((p) => rotatePt(p, r)) : pts, rb = bbox(rp), b = shift(rp, -rb.minX, -rb.minY);
    const cb = shift(r ? coarse.map((p) => rotatePt(p, r)) : coarse, -rb.minX, -rb.minY);
    const consider = (dx, dy) => { const world = shift(b, dx, dy); const s = score(bbox(worlds.concat(world)), world); if (!best || s < best.s) best = { s, rot: r, x: dx - rb.minX, y: dy - rb.minY, world }; };
    for (let i = 0; i <= AUTO_STEPS; i++) {
      if (axes.includes(0)) {
        const ty = ab.minY - rb.h + (ab.h + rb.h) * (i / AUTO_STEPS);
        for (const [far, near] of [[ab.maxX + c, ab.minX - rb.w], [ab.minX - rb.w - c, ab.maxX]]) { const d = slide(inf, [cb], 0, ty, far, near); if (d !== null) consider(d, ty); }
      }
      if (axes.includes(1)) {
        const tx = ab.minX - rb.w + (ab.w + rb.w) * (i / AUTO_STEPS);
        for (const [far, near] of [[ab.maxY + c, ab.minY - rb.h], [ab.minY - rb.h - c, ab.maxY]]) { const d = slide(inf, [cb], 1, tx, far, near); if (d !== null) consider(tx, d); }
      }
    }
  }
  return best;
}
const anchor = (pts, rot = 0) => { const rp = rot ? pts.map((p) => rotatePt(p, rot)) : pts, b = bbox(rp); return { rot, x: -b.minX, y: -b.minY, world: shift(rp, -b.minX, -b.minY) }; };
const normalize = (placed) => { const bb = bbox(flat(placed)); return pack(placed.map((p) => ({ ...p, x: p.x - bb.minX, y: p.y - bb.minY, world: shift(p.world, -bb.minX, -bb.minY) }))); };
const sameBelt = (a, b) => straightEdgeOnTop(a) === straightEdgeOnTop(b);

// B / C — doors stacked across the film (belt-to-belt as today, or nose-to-tail with the right door turned so its deep end sits over
// the tapered end), then each pane turned and slid into whichever open corner adds the least film.
function doorsThenPanes(dL, dR, panes, flipR, spacing, maxWidth) {
  const film = (bb) => filmScore(bb, maxWidth);
  const placed = [anchor(dL)];
  const door = placeAround(placed, dR, [flipR], film, spacing, [1]);
  if (!door) return null;
  placed.push(door);
  for (const pts of panes) { const b = placeAround(placed, pts, [0, 180, 90, 270], film, spacing); if (!b) return null; placed.push(b); }
  return normalize(placed);  // order: dL, dR, panes…
}
// D — free search: every piece tried at 0 / 90 / 180 / 270, slid in from all four sides — shortest film wins.
function freeSearch(list, spacing, maxWidth) {
  const film = (bb) => filmScore(bb, maxWidth);
  const placed = [anchor(list[0])];
  for (const pts of list.slice(1)) { const b = placeAround(placed, pts, [0, 90, 180, 270], film, spacing); if (!b) return null; placed.push(b); }
  return normalize(placed);
}
// E — each side on its belt line (pane never taller than the door), the right side turned 180° and slid across so the door slants mesh.
function sidesMeshed(dL, qL, dR, qR, spacing) {
  const row = (door, pane, rot) => {
    const d = anchor(door, rot), H = bbox(d.world).h;
    const q = placeAround([d], pane, [rot], (bb) => bb.w * 100 + Math.max(0, bb.h - H) * 1000, spacing, [0]);
    return q ? [d, q] : null;
  };
  const left = row(dL, qL, 0), right = row(dR, qR, 180);
  if (!left || !right) return null;
  const inf = left.map((p) => offsetPolygon(p.world, spacing)), ab = bbox(flat(left)), rb = bbox(flat(right)), c = clear(spacing);
  let best = null;
  for (let i = 0; i <= 24; i++) {
    const dx = ab.minX - rb.w * 0.5 + ab.w * (i / 24) - rb.minX;
    const polys = right.map((p) => shift(p.world, dx, 0));
    const dy = slide(inf, polys, 1, 0, ab.maxY + c, ab.minY - rb.h);
    if (dy === null) continue;
    const worlds = polys.map((w) => shift(w, 0, dy)), bb = bbox(flat(left).concat(worlds.flat()));
    const s = bb.h * 100 + bb.w;
    if (!best || s < best.s) best = { s, dx, dy, worlds };
  }
  if (!best) return null;
  const moved = right.map((p, i) => ({ ...p, x: p.x + best.dx, y: p.y + best.dy, world: best.worlds[i] }));
  return normalize([left[0], moved[0], left[1], moved[1]]);  // order: dL, dR, qL, qR
}

const _autoCache = new Map();
const AUTO_CACHE_MAX = 64;
function memoLayout(key, make) {
  if (_autoCache.has(key)) return _autoCache.get(key);
  const lay = make();
  if (_autoCache.size >= AUTO_CACHE_MAX) _autoCache.delete(_autoCache.keys().next().value);
  _autoCache.set(key, lay);
  return lay;
}
const ptsKey = (pts) => { let h = pts.length; for (let i = 0; i < pts.length; i += Math.max(1, pts.length >> 4)) h = (h * 31 + pts[i][0] * 1000 + pts[i][1] * 7) % 1e9; return `${pts.length}:${h.toFixed(0)}`; };
const sideOrder = (list) => [...list].sort((a, b) => Math.abs(polygonArea(b.p.points)) - Math.abs(polygonArea(a.p.points)));  // door first, pane second

// Left door + pane and right door + pane as one block: today's belt-row stack (A) plus the tighter packings B–E, keep whichever uses
// the least film (never longer than today). When today's block is too wide for the film, a block that fits across wins as long
// as it is not more than 30 % longer (otherwise the roll-width prompt stays the better answer). Placements in order dL, qL, dR, qR.
export function autoComboLayout(dL, qL, dR, qR, spacing = 0.25, maxWidth = 1e9) {
  const current = mirrorStack(beltLayout([dL, qL], spacing), beltLayout([dR, qR], spacing), spacing);
  const reorder = (lay, idx) => lay && { ...pack(idx.map((i) => lay.placed[i])) };  // → dL, qL, dR, qR
  const flipB = sameBelt(dL, dR) ? 180 : 0;
  const options = [
    reorder(doorsThenPanes(dL, dR, [qL, qR], flipB, spacing, maxWidth), [0, 2, 1, 3]),
    reorder(doorsThenPanes(dL, dR, [qL, qR], 180 - flipB, spacing, maxWidth), [0, 2, 1, 3]),
    reorder(freeSearch([dL, dR, qL, qR], spacing, maxWidth), [0, 2, 1, 3]),
    reorder(sidesMeshed(dL, qL, dR, qR, spacing), [0, 2, 1, 3]),
  ].filter(Boolean);
  const fits = (lay) => lay.h <= maxWidth + 1e-6;
  const cap = fits(current) ? current.w - 0.05 : current.w * 1.3;
  let best = current;
  for (const lay of options) if (fits(lay) && lay.w <= cap && (best === current || filmScore(lay, maxWidth) < filmScore(best, maxWidth))) best = lay;
  return normalize(best.placed);
}
// One side: the belt row as today, or the pane turned to mesh with the door's slanted end — never taller than the row is today —
// when that is shorter.
export function autoSideLayout(door, pane, spacing = 0.25, maxWidth = 1e9) {
  const current = beltLayout([door, pane], spacing);
  const d = anchor(door), H = Math.max(bbox(d.world).h, current.h);
  const q = placeAround([d], pane, [0, 180, 90, 270], (bb) => bb.w * 100 + Math.max(0, bb.h - H) * 1000, spacing);
  const tucked = q ? normalize([d, q]) : null;
  return normalize((tucked && tucked.h <= H + 0.05 && tucked.w < current.w - 0.05 && tucked.h <= Math.max(maxWidth, current.h) + 1e-6 ? tucked : current).placed);
}

// Combo rows: left + right pieces → auto-nested block (door + pane each side, see autoComboLayout); one side only → belt row or tucked;
// anything else → the generic tight block. Placements come back in input order. `quick` answers at once with today's belt layout
// (flagged `quick: true`) when the auto nesting is not cached yet — the sidebar upgrades the row in idle time (see warmComboLayout).
export function comboLayout(pieces, spacing = 0.25, maxWidth = 1e9, quick = false) {
  const L = pieces.map((p, i) => ({ p, i })).filter((o) => o.p.side === "left"), R = pieces.map((p, i) => ({ p, i })).filter((o) => o.p.side === "right");
  if (L.length + R.length !== pieces.length || (!L.length && !R.length)) return groupLayout(pieces.map((p) => p.points), spacing, maxWidth);
  let order = L.concat(R), lay;
  const belt = () => (L.length && R.length ? mirrorStack(beltLayout(L.map((o) => o.p.points), spacing), beltLayout(R.map((o) => o.p.points), spacing), spacing) : beltLayout(order.map((o) => o.p.points), spacing));
  const auto = (kind, make) => {
    const key = `${kind}|${spacing}|${maxWidth}|${order.map((o) => `${o.p.id}:${ptsKey(o.p.points)}`).join("|")}`;
    if (quick && !_autoCache.has(key)) { const b = belt(); return b && { ...b, quick: true }; }
    return memoLayout(key, make);
  };
  if (L.length === 2 && R.length === 2) {
    order = sideOrder(L).concat(sideOrder(R));
    const [dL, qL, dR, qR] = order.map((o) => o.p.points);
    lay = auto("lr", () => autoComboLayout(dL, qL, dR, qR, spacing, maxWidth));
  } else if (L.length + R.length === 2 && (!L.length || !R.length)) {
    order = sideOrder(order);
    const [door, pane] = order.map((o) => o.p.points);
    lay = auto("one", () => autoSideLayout(door, pane, spacing, maxWidth));
  } else {
    lay = belt();
  }
  if (!lay) return null;
  const placed = [], worlds = [];
  order.forEach((o, k) => { placed[o.i] = lay.placed[k]; worlds[o.i] = lay.worlds[k]; });
  return { ...lay, placed, worlds };
}
export const warmComboLayout = (pieces, spacing, maxWidth) => comboLayout(pieces, spacing, maxWidth, false);

// Which edge of a side pane leans in toward the roof: "left" / "right" / null (symmetric centre pane).
export function slantSide(pts) {
  const bb = bbox(pts);
  if (bb.h < 1 || pts.length < 8) return null;
  const top = pts.filter((p) => p[1] < bb.minY + bb.h * 0.25), bot = pts.filter((p) => p[1] > bb.maxY - bb.h * 0.25);
  if (top.length < 2 || bot.length < 2) return null;
  const minX = (P) => Math.min(...P.map((p) => p[0])), maxX = (P) => Math.max(...P.map((p) => p[0]));
  const leftIn = minX(top) - minX(bot), rightIn = maxX(bot) - maxX(top), thr = Math.max(0.4, bb.h * 0.12);
  if (leftIn > thr && leftIn > rightIn * 2) return "left";
  if (rightIn > thr && rightIn > leftIn * 2) return "right";
  return null;
}

// Upper boundary of an outline sampled across x (y grows downward, so the top is the smallest y in each column).
function topContour(pts, x0, x1, bins) {
  const dense = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length], n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 0.2));
    for (let k = 0; k < n; k++) dense.push([a[0] + (b[0] - a[0]) * (k / n), a[1] + (b[1] - a[1]) * (k / n)]);
  }
  const out = new Array(bins).fill(null), step = (x1 - x0) / bins;
  for (const p of dense) { const i = Math.floor((p[0] - x0) / step); if (i >= 0 && i < bins && (out[i] === null || p[1] < out[i])) out[i] = p[1]; }
  return out;
}

// Stretch every part of a multi-part row by one factor along an axis so the row measures `n` inches: L = parts side by side
// (gaps stay fixed), W = the tallest part. Returns the new pieces array, or an error string.
export function stretchSet(pieces, ids, axis, n, gap, layout) {
  if (!(n >= 0.5) || n > 120) return "Enter a size between 0.5 and 120 inches";
  const gaps = axis === "L" ? (ids.length - 1) * gap : 0;
  const cur = axis === "L" ? layout.w : layout.h;
  if (n - gaps < 0.5 * ids.length) return `Too small for ${ids.length} parts`;
  const k = (n - gaps) / (cur - gaps);
  if (Math.abs(k - 1) < 1e-4) return null;
  const stretch = (p) => {
    const bb = bbox(p.points);
    const sc = ([x, y]) => (axis === "L" ? [bb.minX + (x - bb.minX) * k, y] : [x, bb.minY + (y - bb.minY) * k]);
    return { ...p, points: p.points.map(sc), basePoints: p.points.map(sc), holes: (p.holes || []).map((h) => h.map(sc)), dirty: true, edited: true, appliedOffset: 0 };
  };
  return pieces.map((p) => (ids.includes(p.id) ? stretch(p) : p));
}

// Eyebrow halves laid on the vehicle's windshield: the row (setLayout order/flip) is centred on the glass and its top contour
// seated on the windshield's top edge (median offset, so the mirror notch does not decide). Returns null without a windshield.
export function stripFit(list, ws, spacing = 0.25) {
  if (!ws || !list?.length) return null;
  const lay = setLayout(list, spacing);
  if (!lay) return null;
  const all = lay.worlds.flat(), rb = bbox(all), wb = bbox(ws.points);
  const dx = (wb.minX + wb.maxX) / 2 - (rb.minX + rb.maxX) / 2;
  const bins = 40, rowTop = topContour(all, rb.minX, rb.maxX, bins), wsTop = topContour(ws.points, rb.minX + dx, rb.maxX + dx, bins);
  const d = rowTop.map((y, i) => (y === null || wsTop[i] === null ? null : wsTop[i] - y)).filter((v) => v !== null).sort((a, b) => a - b);
  const dy = d.length ? d[Math.floor(d.length / 2)] : wb.minY - rb.minY;
  const worlds = lay.worlds.map((w) => shift(w, dx, dy));
  // + = film edge sits below the glass edge (gap), − = film sticks out past the glass (overhang), per column along the row
  const gaps = rowTop.map((y, i) => (y === null || wsTop[i] === null ? null : y + dy - wsTop[i])).filter((v) => v !== null);
  const rowBox = bbox(worlds.flat());
  const abs = gaps.map((g) => Math.abs(g)).sort((a, b) => a - b);
  return { worlds, glass: ws.points, wb, rowBox, w: rb.w, h: rb.h, gaps, typical: abs.length ? abs[Math.floor(abs.length / 2)] : 0, maxGap: Math.max(0, ...gaps), maxOverhang: Math.max(0, ...gaps.map((g) => -g)), ends: [rowBox.minX - wb.minX, wb.maxX - rowBox.maxX] };
}

// Windshield eyebrow halves: decide order and orientation from the vehicle's own windshield — every order × flip of the halves is
// laid in a row and its top contour compared with the windshield's top edge (centred on the glass; the mirror notch is ignored).
// Windshield eyebrow halves: orientation comes from the half's own shape (the straight sill edge is the bottom — never turned over
// just to fit a contour), and for two halves the tall glass-corner ends go outward. Only when that is ambiguous (ends alike, or
// 3-4 parts) is the row's top contour compared with the vehicle's windshield top edge to pick the order.
// Marks each piece with setRank / setRot for setLayout; pieces that stay undecided keep the geometric fallback.
export function arrangeStripSets(pieces) {
  const ws = pieces.filter((p) => p.pieceType === "windshield" && p.points?.length > 8).sort((a, b) => bbox(b.points).w - bbox(a.points).w)[0];
  const groups = new Map();
  for (const p of pieces) if (p.pieceType === "windshield_strip" && p.setKey) groups.set(p.setKey, [...(groups.get(p.setKey) || []), p]);
  const perms = (arr) => (arr.length <= 1 ? [arr] : arr.flatMap((v, i) => perms([...arr.slice(0, i), ...arr.slice(i + 1)]).map((r) => [v, ...r])));
  const decided = new Map();
  for (const list of groups.values()) {
    if (list.length < 2 || list.length > 4) continue;
    const flips = list.map((p) => straightEdgeOnTop(p.points));
    const prepared = list.map((p, i) => (flips[i] ? p.points.map((q) => rotatePt(q, 180)) : p.points));
    const tall = prepared.map((pts) => centerEnd(pts));
    let order = null;
    if (list.length === 2 && tall[0] && tall[1] && tall[0] !== tall[1]) order = tall[0] === "left" ? [0, 1] : [1, 0];
    if (!order && ws) {
      const wb = bbox(ws.points), wsCenter = (wb.minX + wb.maxX) / 2;
      let best = null;
      for (const ord of perms(list.map((_, i) => i))) {
        const worlds = [];
        let cursor = 0;
        const H = Math.max(...prepared.map((pts) => bbox(pts).h));
        for (const i of ord) { const b = bbox(prepared[i]); worlds.push(shift(prepared[i], cursor - b.minX, H - b.maxY)); cursor += b.w + 0.25; }
        const all = worlds.flat(), rb = bbox(all), dx = wsCenter - (rb.minX + rb.maxX) / 2;
        const bins = 40, rowTop = topContour(all, rb.minX, rb.maxX, bins), wsTop = topContour(ws.points, rb.minX + dx, rb.maxX + dx, bins);
        const r = rowTop.map((y, i) => (y === null || wsTop[i] === null ? null : y - wsTop[i])).filter((v) => v !== null).sort((a, b) => a - b);
        if (r.length < bins / 2) continue;
        const med = r[Math.floor(r.length / 2)];
        const err = r.reduce((s, v) => s + Math.min(Math.abs(v - med), 0.5), 0) / r.length;  // capped: the mirror notch must not decide
        if (!best || err < best.err) best = { err, ord };
      }
      if (best) order = best.ord;
    }
    if (!order) continue;
    order.forEach((i, rank) => decided.set(list[i].id, { setRank: rank, setRot: flips[i] ? 180 : 0 }));
  }
  return decided.size ? pieces.map((p) => (decided.has(p.id) ? { ...p, ...decided.get(p.id) } : p)) : pieces;
}


// Which end of a windshield-eyebrow half is the tall (pillar) end: "left" / "right", or null when both ends look alike.
export function centerEnd(pts) {
  const bb = bbox(pts);
  if (bb.w < 1 || pts.length < 8) return null;
  const span = (P) => (P.length < 2 ? 0 : Math.max(...P.map((p) => p[1])) - Math.min(...P.map((p) => p[1])));
  const l = span(pts.filter((p) => p[0] < bb.minX + bb.w * 0.15)), r = span(pts.filter((p) => p[0] > bb.maxX - bb.w * 0.15));
  if (r > l * 1.05) return "right";
  if (l > r * 1.05) return "left";
  return null;
}

// 3-piece slider the way DigiCut shows it: panes in one row, bottoms level, slanted edges outward (lean-left pane first,
// centre, lean-right pane last). Panes are drawn as seen from inside the cab, so the pane named "Right" sits on the left.
// Eyebrow halves: tall (pillar) ends outward, the short corners meet in the middle — the half whose tall end is on its left goes first.
// A half stored upside down (straight sill edge at the top) is turned 180° so the row sits like the strip does on the glass.
const edgeBins = (pts) => {
  const bb = bbox(pts);
  const dense = [];  // walk the outline so sparse vertices still fill every column
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length], n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 0.25));
    for (let k = 0; k < n; k++) dense.push([a[0] + (b[0] - a[0]) * (k / n), a[1] + (b[1] - a[1]) * (k / n)]);
  }
  const x0 = bb.minX + bb.w * 0.2, x1 = bb.maxX - bb.w * 0.2, bins = 16, top = [], bot = [];
  for (let i = 0; i < bins; i++) {
    const lo = x0 + ((x1 - x0) * i) / bins, hi = lo + (x1 - x0) / bins;
    const ys = dense.filter((p) => p[0] >= lo && p[0] < hi).map((p) => p[1]);
    if (ys.length) { top.push([lo, Math.min(...ys)]); bot.push([lo, Math.max(...ys)]); }
  }
  return { top, bot };
};
const lineFit = (P) => {  // slope + residual of the best-fit line: a straight sill edge ≈ 0 residual, the roof arc ≫ 0
  const n = P.length; if (n < 4) return { m: 0, rms: 0 };
  const mx = P.reduce((s, p) => s + p[0], 0) / n, my = P.reduce((s, p) => s + p[1], 0) / n;
  const sxx = P.reduce((s, p) => s + (p[0] - mx) ** 2, 0), sxy = P.reduce((s, p) => s + (p[0] - mx) * (p[1] - my), 0);
  const m = sxx ? sxy / sxx : 0;
  return { m, rms: Math.sqrt(P.reduce((s, p) => s + (p[1] - (my + m * (p[0] - mx))) ** 2, 0) / n) };
};
export const straightEdgeOnTop = (pts) => {
  const bb = bbox(pts);
  if (bb.w < 2 || bb.h < 0.5) return false;
  const { top, bot } = edgeBins(pts);
  return lineFit(top).rms * 1.5 < lineFit(bot).rms;
};
// Tilt (deg) of an eyebrow half's straight sill edge. Halves are traced one at a time so each sits at its own small angle;
// levelling every half on its sill is what lets the row line up like the strip does on the glass.
export const sillTilt = (pts) => {
  const bb = bbox(pts);
  if (bb.w < 2 || bb.h < 0.5) return 0;
  const deg = (Math.atan(lineFit(edgeBins(pts).bot).m) * 180) / Math.PI;
  return Math.abs(deg) > 0.15 && Math.abs(deg) <= 8 ? deg : 0;
};
const levelOnSill = (pts, rot) => {
  const t = sillTilt(pts);
  if (!t) return { pts, rot };
  const a = pts.map((q) => rotatePt(q, -t)), b = pts.map((q) => rotatePt(q, t));
  const norm = (d) => ((d % 360) + 360) % 360;
  if (Math.min(bbox(a).h, bbox(b).h) >= bbox(pts).h) return { pts, rot };  // neither turn makes the strip flatter: leave it
  return bbox(a).h <= bbox(b).h ? { pts: a, rot: norm(rot - t) } : { pts: b, rot: norm(rot + t) };
};
export function setLayout(pieces, spacing = 0.25) {
  const rot180 = (pts) => pts.map((p) => rotatePt(p, 180));
  const prepared = pieces.map((p) => {
    const flip = p.setRot !== undefined ? p.setRot === 180 : p.pieceType === "windshield_strip" && straightEdgeOnTop(p.points);
    const base = { rot: flip ? 180 : 0, pts: flip ? rot180(p.points) : p.points };
    return p.pieceType === "windshield_strip" ? levelOnSill(base.pts, base.rot) : base;
  });
  const rank = (p, i) => {
    if (p.setRank !== undefined) return p.setRank;  // decided from the vehicle's own windshield (arrangeStripSets)
    if (p.pieceType === "windshield_strip") { const c = centerEnd(prepared[i].pts); return c === "left" ? 0 : c === "right" ? 2 : 1; }
    if (p.side === "center") return 1; const s = slantSide(p.points); if (s) return s === "left" ? 0 : 2; return p.side === "right" ? 0 : p.side === "left" ? 2 : 1;
  };
  const order = pieces.map((p, i) => ({ p, i })).sort((a, b) => rank(a.p, a.i) - rank(b.p, b.i) || a.i - b.i);
  const H = Math.max(...prepared.map((q) => bbox(q.pts).h));
  let cursor = 0;
  const placed = [];
  for (const { i } of order) {
    const b = bbox(prepared[i].pts);
    const dx = cursor - b.minX, dy = H - b.maxY;
    placed[i] = { rot: prepared[i].rot, x: dx, y: dy, world: shift(prepared[i].pts, dx, dy) };
    cursor += b.w + spacing;
  }
  const all = placed.flatMap((p) => p.world);
  const bb = bbox(all);
  return { placed, hull: convexHull(all), w: bb.w, h: bb.h, worlds: placed.map((p) => p.world) };
}

// Multi-part template exactly as it was drawn in the uploaded file (every part carries its offset inside the file).
export function fileLayout(pieces, spacing = 0.25, asDrawn = false) {
  if (pieces.length < 2 || !pieces.every((p) => Array.isArray(p.setOffset) && p.setOffset.length === 2)) return null;
  const minX = Math.min(...pieces.map((p) => p.setOffset[0])), minY = Math.min(...pieces.map((p) => p.setOffset[1]));
  const placed = pieces.map((p) => { const dx = p.setOffset[0] - minX, dy = p.setOffset[1] - minY; return { rot: 0, x: dx, y: dy, world: shift(p.points, dx, dy) }; });
  // parts drawn on top of each other → the file had no real arrangement. A strip tucked into a kit's pocket (DigiCut mirror / fender sets) only
  // grazes the big part's traced edge, so the test is "the smaller part's centre lies inside the bigger one", not a mere edge touch
  const centre = (w) => { const b = bbox(w); return [b.cx, b.cy]; };
  for (let i = 0; i < placed.length && !asDrawn; i++) for (let j = i + 1; j < placed.length; j++) {
    const a = bbox(placed[i].world), b = bbox(placed[j].world);
    if (Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX) <= 0.5 || Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY) <= 0.5) continue;
    const [big, small] = a.w * a.h >= b.w * b.h ? [placed[i].world, placed[j].world] : [placed[j].world, placed[i].world];
    if (pointInPoly(centre(small), big) && polysIntersect(big, small)) return null;
  }
  const all = placed.flatMap((p) => p.world), bb = bbox(all);
  return { placed, hull: convexHull(all), w: bb.w, h: bb.h, worlds: placed.map((p) => p.world) };
}

// 3-piece slider the way DigiCut nests it for the Mazda B-Series: every pane turned 270° (roof edges to the left, so the pane height
// runs along the film), the two side panes stacked in one column with their slanted outer edges top and bottom, the centre pane
// standing beside them, centred.
export function sliderStackLayout(pieces, spacing = 0.25) {
  // file order (left pane, centre, right pane) when offsets exist; otherwise the lean of the outer edges; last resort "Part N"
  const partNo = (p) => Number((p.name || "").match(/(?:Part|Option)\s+(\d+)/i)?.[1] || 0);
  const byFile = pieces.every((p) => Array.isArray(p.setOffset)) ? pieces.map((p, i) => ({ p, i })).sort((a, b) => a.p.setOffset[0] - b.p.setOffset[0]).map((o) => o.i).reverse() : null;
  const rank = (p) => { const s = slantSide(p.points); return s === "right" ? 0 : s === "left" ? 2 : 1; };
  const order = byFile || pieces.map((p, i) => ({ p, i })).sort((a, b) => rank(a.p) - rank(b.p) || partNo(b.p) - partNo(a.p) || b.i - a.i).map((o) => o.i);
  const turned = pieces.map((p) => p.points.map((q) => rotatePt(q, 270))), tipped = turned.map(norm);
  const [top, mid, bot] = order;
  const colW = Math.max(bbox(tipped[top]).w, bbox(tipped[bot]).w), colH = bbox(tipped[top]).h + spacing + bbox(tipped[bot]).h;
  const at = (i, dx, dy) => { const rb = bbox(turned[i]); return { rot: 270, x: dx - rb.minX, y: dy - rb.minY, world: shift(tipped[i], dx, dy) }; };
  const placed = [];
  placed[top] = at(top, 0, 0);
  placed[bot] = at(bot, 0, bbox(tipped[top]).h + spacing);
  placed[mid] = at(mid, colW + spacing, Math.max(0, (colH - bbox(tipped[mid]).h) / 2));
  return { ...pack(placed), stacked: true };
}

// Any multi-part set on the roll: eyebrows / slider back glass keep their vehicle-aware row; every other set (2-part roll-up
// window, split quarter …) sits exactly as in the file, or — for older uploads without offsets — Part 1, Part 2 … left to right.
// PPF / decal / nav-screen sets: one horizontal row along the roll, every part turned so its long side runs with the film,
// bottoms levelled — the owner asked for the whole kit laid out across the grid instead of "as drawn" in DigiCut.
const isPanelPiece = (p) => /^ppf_/.test(p.pieceType || "") || ["decal", "nav_screen", "gauge_cluster"].includes(p.pieceType);
export function rowLayout(pieces, spacing = 0.25, maxH = Infinity) {
  // every part long-side along the roll, then column-packed across the film (height ≤ roll width), columns marching along the length
  const prepared = pieces.map((p, i) => { const b = bbox(p.points); const rot = b.h > b.w ? 90 : 0; const pts = rot ? p.points.map((q) => rotatePt(q, rot)) : p.points; return { i, rot, pts, b: bbox(pts) }; });
  const order = [...prepared].sort((a, b) => b.b.h - a.b.h || b.b.w - a.b.w);
  const placed = [];
  let colX = 0, colW = 0, y = 0;
  for (const q of order) {
    if (y > 0 && y + q.b.h > maxH) { colX += colW + spacing; colW = 0; y = 0; }
    const dx = colX - q.b.minX, dy = y - q.b.minY;
    placed[q.i] = { rot: q.rot, x: dx, y: dy, world: shift(q.pts, dx, dy) };
    y += q.b.h + spacing; colW = Math.max(colW, q.b.w);
  }
  const all = placed.flatMap((p) => p.world), bb = bbox(all);
  return { placed, hull: convexHull(all), w: bb.w, h: bb.h, worlds: placed.map((p) => p.world) };
}

export function partsLayout(pieces, spacing = 0.25, maxH = Infinity) {
  // PPF / decal / nav kits: exactly as drawn in DigiCut (fileLayout below) — the owner wants the list and the roll to match the screenshot;
  // rowLayout (column-packed across the film) is only the fallback when a kit carries no drawn positions
  if (pieces.length > 1 && pieces.every(isPanelPiece)) return fileLayout(pieces, spacing, true) || rowLayout(pieces, spacing, maxH);  // a traced screenshot cannot hold overlapping parts: its positions are always the truth
  if (pieces.length === 3 && pieces.every((p) => p.sliderStack && p.pieceType === "back_glass")) return sliderStackLayout(pieces, spacing);
  if (pieces.every((p) => p.pieceType === "windshield_strip")) return setLayout(pieces, spacing);  // eyebrow halves: levelled on the sill, bottoms in one line, tall ends out — never "as drawn"
  const f = fileLayout(pieces, spacing);  // the uploaded file is the truth for every other set (DigiCut shows exactly this)
  if (f) return f;
  if (pieces.every((p) => p.pieceType === "back_glass")) return setLayout(pieces, spacing);
  const partNo = (p) => Number((p.name || "").match(/(?:Part|Option)\s+(\d+)/i)?.[1] || 0);
  const order = pieces.map((p, i) => ({ p, i })).sort((a, b) => partNo(a.p) - partNo(b.p) || a.i - b.i);
  if (pieces[0]?.side === "right") order.reverse();  // mirror of the left window: the strip swaps sides
  const H = Math.max(...pieces.map((p) => bbox(p.points).h));
  let cursor = 0;
  const placed = [];
  for (const { i } of order) { const b = bbox(pieces[i].points); const dx = cursor - b.minX, dy = H - b.maxY; placed[i] = { rot: 0, x: dx, y: dy, world: shift(pieces[i].points, dx, dy) }; cursor += b.w + spacing; }
  const all = placed.flatMap((p) => p.world), bb = bbox(all);
  return { placed, hull: convexHull(all), w: bb.w, h: bb.h, worlds: placed.map((p) => p.world) };
}

// Nest the group as one block around the pieces already on the roll, then unpack every placement.
export function addGroupToRoll(pieces, ids, layout, opts, fixedWorlds) {
  const res = nestPieces([{ id: "group", points: layout.hull }], opts, fixedWorlds);
  if (res.overflow.length) return { pieces, overflow: ids };
  const { x: X, y: Y, rotation: R } = res.placements[0];
  const map = {};
  ids.forEach((id, i) => { const p = layout.placed[i]; const [ox, oy] = rotatePt([p.x, p.y], R); map[id] = { rotation: (p.rot + R) % 360, x: X + ox, y: Y + oy }; });
  return { overflow: [], pieces: pieces.map((p) => (map[p.id] ? { ...p, included: true, ...map[p.id] } : p)) };
}
